import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock } from 'lucide-react';
import Header from '../components/Header';

import Footer from '../components/Footer';
import SEO from '../components/SEO';
import blogPosts from '../data/blogPosts';

function RenderBlock({ block }) {
  if (block.type === 'heading') {
    return <h2 className="text-xl font-bold text-gray-900 mt-10 mb-4">{block.text}</h2>;
  }
  if (block.type === 'paragraph') {
    return (
      <div className="mb-5">
        <p className="text-[15px] leading-[1.8] text-gray-700">{block.text}</p>
        {block.source && (
          <a href={block.source.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline mt-1 inline-block">
            Source: {block.source.name}
          </a>
        )}
      </div>
    );
  }
  if (block.type === 'callout') {
    return (
      <div className="my-6 border-l-4 border-blue-500 bg-blue-50 rounded-r-lg px-5 py-4">
        <p className="text-sm text-blue-900 leading-relaxed italic">{block.text}</p>
      </div>
    );
  }
  if (block.type === 'list') {
    return (
      <ol className="my-5 space-y-3 list-decimal list-inside">
        {block.items.map((item, i) => (
          <li key={i} className="text-[15px] text-gray-700 leading-relaxed">{item}</li>
        ))}
      </ol>
    );
  }
  if (block.type === 'table') {
    return (
      <div className="my-6 overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-sm" data-testid="blog-table">
          <thead>
            <tr className="bg-gray-50">
              {block.headers.map((h, i) => (
                <th key={i} className="text-left px-4 py-3 font-semibold text-gray-700 border-b">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, ri) => (
              <tr key={ri} className="border-b border-gray-100 last:border-0">
                {row.map((cell, ci) => (
                  <td key={ci} className={`px-4 py-3 text-gray-600 ${ci === 0 ? 'font-medium text-gray-900' : ''}`}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return null;
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.id === slug);
  const currentIndex = blogPosts.findIndex((p) => p.id === slug);
  const nextPost = blogPosts[currentIndex + 1];
  const prevPost = blogPosts[currentIndex - 1];

  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="pt-32 pb-20 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Post not found</h1>
          <Link to="/blog" className="text-blue-600 hover:underline">Back to blog</Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.id}`}
        type="article"
        article={{
          title: post.title,
          excerpt: post.excerpt,
          datePublished: post.date,
        }}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Blog', path: '/blog' },
          { name: post.title, path: `/blog/${post.id}` },
        ]}
      />
      <main className="pt-44 pb-20">
        <article className="max-w-[680px] mx-auto px-6">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-blue-600 transition-colors mb-8" data-testid="blog-back">
            <ArrowLeft className="w-4 h-4" />
            All posts
          </Link>

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-blue-50 text-blue-700">{post.category}</span>
              <span className="flex items-center gap-1 text-xs text-gray-400">
                <Clock className="w-3 h-3" />
                {post.readTime}
              </span>
              <span className="text-xs text-gray-400">{post.date}</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight" data-testid="blog-post-title">
              {post.title}
            </h1>
          </div>

          <div data-testid="blog-post-content">
            {post.content.map((block, i) => (
              <RenderBlock key={i} block={block} />
            ))}
          </div>

          {/* CTA */}
          <div className="mt-14 border border-gray-200 rounded-xl p-6 bg-gray-50 text-center">
            <p className="text-sm font-semibold text-gray-900 mb-1">Tired of dashboards that don't drive action?</p>
            <p className="text-xs text-gray-500 mb-4">See how Forsyt turns pipeline data into daily directives.</p>
            <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 px-5 py-2.5 rounded-lg transition-colors" data-testid="blog-cta">
              Try the Sandbox
            </Link>
          </div>

          {/* Internal Links - Related Reading */}
          <div className="mt-10 pt-8 border-t border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Related reading</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {blogPosts
                .filter((p) => p.id !== post.id)
                .slice(0, 4)
                .map((related) => (
                  <Link
                    key={related.id}
                    to={`/blog/${related.id}`}
                    className="text-sm text-gray-600 hover:text-blue-600 transition-colors py-2 border-b border-gray-50"
                  >
                    {related.title}
                  </Link>
                ))}
              <Link to="/vs-gong" className="text-sm text-gray-600 hover:text-blue-600 transition-colors py-2 border-b border-gray-50">
                Forsyt vs Gong: Full Comparison
              </Link>
              <Link to="/vs-clari" className="text-sm text-gray-600 hover:text-blue-600 transition-colors py-2 border-b border-gray-50">
                Forsyt vs Clari: Full Comparison
              </Link>
            </div>
          </div>

          {/* Prev / Next navigation */}
          <div className="mt-10 pt-8 border-t border-gray-100 flex items-center justify-between">
            {prevPost ? (
              <Link to={`/blog/${prevPost.id}`} className="group text-left">
                <p className="text-xs text-gray-400 mb-0.5">Previous</p>
                <p className="text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors">{prevPost.title}</p>
              </Link>
            ) : <div />}
            {nextPost ? (
              <Link to={`/blog/${nextPost.id}`} className="group text-right">
                <p className="text-xs text-gray-400 mb-0.5">Next</p>
                <p className="text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors">{nextPost.title}</p>
              </Link>
            ) : <div />}
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}
